/* ═══════════════════════════════════════════════════════════════
   SpecMasterService — Spec Master + Draft CRUD via EC2 API (cloud, in-memory cache)
   ═══════════════════════════════════════════════════════════════ */

import type { SpecMaster, SpecMasterDraft } from '../../shared/types';
import * as cache from './cloudCache';
import type { BatchWrite, TableOpts } from './cloudCache';
import * as cloudApi from './apiClient';
import { getCurrentFirmId } from './firmContext';
import { log as logActivity } from './activityLogger';

// `slots` / `layers` are the Masters Ladder R1 columns (migration 044). They
// round-trip exactly like `variants`: jsonb on the server, parsed objects in
// the cache. Absent on a master that has never been migrated or authored —
// cloudCache only sends keys the record actually has, so nothing is nulled.
const masterOpts: TableOpts = {
  jsonFields: [
    'coreContent',
    'systems',
    'variants',
    'slots',
    'layers',
    'tags',
    'versionHistory',
    'editHistory',
  ],
};

const draftOpts: TableOpts = {
  jsonFields: ['coreContent', 'systems', 'variants', 'slots', 'layers', 'publishRequest'],
  columnMap: {
    blueprint_id: 'spec_master_id',
  },
};

export const SpecMasterService = {
  /* ── Published Spec Masters ── */
  list: async (): Promise<SpecMaster[]> => cache.list<SpecMaster>('spec_masters', masterOpts),

  get: async (id: string): Promise<SpecMaster | null> => {
    const all = await SpecMasterService.list();
    return all.find((m) => m.id === id) || null;
  },

  save: async (master: SpecMaster): Promise<SpecMaster> => {
    const isNew = !master.id;
    const saved = await cache.save('spec_masters', master, masterOpts);
    logActivity({
      action: isNew ? 'spec.publish' : 'spec.update',
      entityType: 'spec_master',
      entityId: saved.id,
      metadata: { title: saved.title, csiNumber: saved.csiNumber },
    });
    return saved;
  },

  delete: async (id: string): Promise<void> => {
    await cache.remove('spec_masters', id);
    logActivity({ action: 'spec.delete', entityType: 'spec_master', entityId: id });
  },

  /** Archive or restore — a THREE-key partial update on purpose: the server
   *  reads an `archived_at` flip of three keys or fewer as an archive toggle
   *  and gates it on `specs.archive`. Mirrors ModuleService.setArchived —
   *  spec_masters carries a `modified_by` column (migration 050 added
   *  `archived_by`), so both are stamped; `last_modified` is left alone to
   *  stay within the server's 3-key threshold. */
  setArchived: async (id: string, archived: boolean, userName?: string): Promise<void> => {
    const firmId = getCurrentFirmId();
    await cloudApi.dbUpdate(
      'spec_masters',
      {
        archived_at: archived ? new Date().toISOString() : null,
        archived_by: archived ? userName || null : null,
        modified_by: userName || null,
      },
      [
        { col: 'firm_id', op: '=', val: firmId },
        { col: 'id', op: '=', val: id },
      ]
    );
    cache.invalidate('spec_masters');
    logActivity({
      action: archived ? 'spec.archive' : 'spec.restore',
      entityType: 'spec_master',
      entityId: id,
    });
  },

  /** Write ONLY the edit history of a published master (Tweaks 36's
   *  "Reverted to v1 (partial)" line) — a partial update, so a publish that
   *  lands meanwhile keeps its text and version. */
  setEditHistory: async (id: string, editHistory: unknown[]): Promise<void> => {
    await cloudApi.dbUpdate('spec_masters', { edit_history: JSON.stringify(editHistory) }, [
      { col: 'firm_id', op: '=', val: getCurrentFirmId() },
      { col: 'id', op: '=', val: id },
    ]);
    cache.invalidate('spec_masters');
  },

  /* ── Drafts ── */
  listDrafts: async (): Promise<SpecMasterDraft[]> =>
    cache.list<SpecMasterDraft>('spec_master_drafts', draftOpts),

  getDraft: async (id: string): Promise<SpecMasterDraft | null> => {
    const all = await SpecMasterService.listDrafts();
    return all.find((d) => d.id === id) || null;
  },

  saveDraft: async (
    draft: SpecMasterDraft,
    o?: { collabMirror?: boolean }
  ): Promise<SpecMasterDraft> => {
    // `ydoc_state` (the live shared document) belongs to the collab server
    // alone — never echo a copy back through the generic save, or a stale
    // one would roll every collaborator back. The server strips it too.
    const { ydocState: _ydocState, ...record } = draft as SpecMasterDraft & { ydocState?: unknown };
    void _ydocState;
    // `collabMirror` marks the editor's own JSON mirror write: the server
    // keeps the live Yjs state for it and resets that state for any other
    // writer of core_content/layers (ec2-api shouldResetCollabState).
    const wire = o?.collabMirror ? { ...record, collabMirror: true } : record;
    const saved = await cache.save('spec_master_drafts', wire as SpecMasterDraft, draftOpts);
    logActivity({
      action: 'spec.draft_save',
      entityType: 'spec_master_draft',
      entityId: saved.id,
    });
    return saved;
  },

  deleteDraft: async (id: string): Promise<void> => cache.remove('spec_master_drafts', id),

  /** Drop the cached masters + drafts so the next read hits the server. A
   *  teammate's publish/discard reaches this window as a collab close, not
   *  always as a realtime row event — the cache would still hold the old
   *  version and the deleted draft row. */
  forgetCached: (): void => {
    cache.invalidate('spec_masters');
    cache.invalidate('spec_master_drafts');
  },

  /** EVERY draft row for a master — the shared one and any older per-user
   *  drafts from before live shared drafts, so none can resurface. */
  draftIdsFor: async (masterId: string): Promise<string[]> =>
    (await SpecMasterService.listDrafts())
      .filter((d) => d.blueprintId === masterId)
      .map((d) => d.id),

  /* ── Batch writes (one server transaction; cloudCache.batch) ── */
  /** The master row as a batch op — the same serializer as `save`. */
  saveWrite: (master: SpecMaster): BatchWrite => ({
    kind: 'save',
    table: 'spec_masters',
    record: master,
    opts: masterOpts,
  }),
  removeDraftWrite: (id: string): BatchWrite => ({
    kind: 'remove',
    table: 'spec_master_drafts',
    id,
  }),
};
